import { createMemo, createSignal, For, Show } from "solid-js"
import type { JSX } from "solid-js"
import { Button } from "@openanalyst-ai/ui/button"

type Column = {
  key: string
  label: string
  sortable?: boolean
  width?: string
  align?: "left" | "center" | "right"
  format?: "text" | "status" | "code" | "date" | "datetime" | "currency" | "url" | "boolean" | "json"
}

type RowAction = {
  id: string
  label: string
  variant?: "primary" | "secondary" | "ghost"
  risk?: "read" | "local_write" | "external_write" | "destructive"
  confirm?: unknown
  disabledReason?: string
}

type ActionState = {
  visible: boolean
  disabled: boolean
  disabledReason?: string
  pending: boolean
  error?: string
  result?: unknown
}

export function TableWidget(props: {
  data: Record<string, unknown>[]
  columns: Column[]
  rowIdKey?: string
  rowActions?: RowAction[]
  emptyState?: { title?: string; description?: string }
  actionState?: (action: RowAction, row: Record<string, unknown>) => ActionState
  onRowAction?: (action: RowAction, row: Record<string, unknown>) => void
  expandedRowVisible?: (row: Record<string, unknown>) => boolean
  renderExpandedRow?: (row: Record<string, unknown>) => JSX.Element
}) {
  const [sortKey, setSortKey] = createSignal<string>("")
  const [sortAsc, setSortAsc] = createSignal(true)

  const sorted = createMemo(() => {
    const key = sortKey()
    if (!key) return props.data
    const dir = sortAsc() ? 1 : -1
    return [...props.data].sort((a, b) => {
      const av = a[key]
      const bv = b[key]
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir
      return String(av).localeCompare(String(bv)) * dir
    })
  })

  const toggleSort = (key: string) => {
    if (sortKey() === key) {
      setSortAsc(!sortAsc())
    } else {
      setSortKey(key)
      setSortAsc(true)
    }
  }

  return (
    <Show
      when={props.data.length}
      fallback={
        <div
          data-dashboard-empty-state=""
          class="rounded-lg border border-dashed border-border-weak-base bg-background-secondary/65 px-3 py-4 text-center text-xs text-text-dimmed"
        >
          <div class="flex flex-col gap-1">
            <span>{props.emptyState?.title ?? "No rows yet"}</span>
            <Show when={props.emptyState?.description}>
              <span class="text-[11px]">{props.emptyState?.description}</span>
            </Show>
          </div>
        </div>
      }
    >
      <div
        data-dashboard-table="viewport"
        class="max-h-[32rem] overflow-auto rounded-md border border-border-weak-base/80 bg-background-base/80"
      >
        <table class="w-full min-w-max border-separate border-spacing-0 text-sm">
          <thead class="sticky top-0 z-10 bg-background-base/95 backdrop-blur">
            <tr class="border-b border-border-dimmed">
              <For each={props.columns}>
                {(col) => (
                  <th
                    class="border-b border-border-dimmed px-3 py-2 text-xs font-medium text-text-dimmed"
                    classList={{
                      "text-left": (col.align ?? "left") === "left",
                      "text-center": col.align === "center",
                      "text-right": col.align === "right",
                      "cursor-pointer select-none hover:text-text-strong": Boolean(col.sortable),
                    }}
                    style={{ width: col.width }}
                    onClick={() => col.sortable && toggleSort(col.key)}
                  >
                    <span class="inline-flex items-center gap-1">
                      {col.label}
                      {col.sortable && sortKey() === col.key && (
                        <span class="text-[10px]">{sortAsc() ? "\u25B2" : "\u25BC"}</span>
                      )}
                    </span>
                  </th>
                )}
              </For>
              <Show when={props.rowActions?.length}>
                <th class="border-b border-border-dimmed px-3 py-2 text-right text-xs font-medium text-text-dimmed">
                  Actions
                </th>
              </Show>
            </tr>
          </thead>
          <tbody>
            <For each={sorted()}>
              {(row) => (
                <>
                  <tr class="hover:bg-background-stronger/30">
                    <For each={props.columns}>
                      {(col) => {
                        const val = () => row[col.key]
                        return (
                          <td
                            class="border-b border-border-dimmed/50 px-3 py-2 text-text-base"
                            classList={{
                              "text-left": (col.align ?? "left") === "left",
                              "text-center": col.align === "center",
                              "text-right": col.align === "right",
                            }}
                          >
                            <CellValue value={val()} column={col} />
                          </td>
                        )
                      }}
                    </For>
                    <Show when={props.rowActions?.length}>
                      <td class="border-b border-border-dimmed/50 px-3 py-2">
                        <div class="flex justify-end gap-2">
                          <For
                            each={(props.rowActions ?? []).filter(
                              (action) => props.actionState?.(action, row).visible ?? true,
                            )}
                          >
                            {(action) => {
                              const state = () => props.actionState?.(action, row)
                              return (
                                <Button
                                  size="small"
                                  variant={action.variant ?? "secondary"}
                                  disabled={Boolean(state()?.disabled || state()?.pending)}
                                  title={
                                    state()?.disabled ? (state()?.disabledReason ?? action.disabledReason) : undefined
                                  }
                                  onClick={() => props.onRowAction?.(action, row)}
                                >
                                  {state()?.pending ? `${action.label}...` : action.label}
                                </Button>
                              )
                            }}
                          </For>
                        </div>
                      </td>
                    </Show>
                  </tr>
                  <Show when={props.renderExpandedRow && (props.expandedRowVisible?.(row) ?? true)}>
                    <tr class="bg-background-secondary/40">
                      <td
                        colspan={props.columns.length + (props.rowActions?.length ? 1 : 0)}
                        class="border-b border-border-dimmed/50 px-3"
                      >
                        {props.renderExpandedRow?.(row)}
                      </td>
                    </tr>
                  </Show>
                </>
              )}
            </For>
          </tbody>
        </table>
      </div>
    </Show>
  )
}

function CellValue(props: { value: unknown; column: Column }) {
  const value = () => props.value
  const [open, setOpen] = createSignal(false)
  const text = () => {
    if (value() === undefined || value() === null) return ""
    if (typeof value() === "boolean") return value() ? "Yes" : "No"
    if (props.column.format === "json") return JSON.stringify(value(), null, 2)
    return String(value())
  }
  if (props.column.format === "status" || props.column.key === "status") return <StatusBadge value={text()} />
  if (props.column.format === "code") return <code class="font-mono text-xs text-text-strong">{text()}</code>
  if (props.column.format === "url" && text()) {
    return (
      <a href={text()} target="_blank" rel="noreferrer" class="text-text-info">
        {text()}
      </a>
    )
  }
  if (props.column.format === "json") {
    return (
      <details
        data-dashboard-json-summary
        class="max-w-72 rounded-md border border-border-weak-base/60 bg-background-secondary/45 px-2 py-1 text-xs"
        onToggle={(event) => setOpen(event.currentTarget.open)}
      >
        <summary class="cursor-pointer list-none text-text-base marker:hidden">{jsonSummary(value())}</summary>
        <Show when={open()}>
          <pre class="mt-2 max-h-40 overflow-auto whitespace-pre-wrap break-words text-xs text-text-base">{text()}</pre>
        </Show>
      </details>
    )
  }
  return <>{text()}</>
}

function jsonSummary(value: unknown) {
  if (Array.isArray(value)) return `${value.length} ${value.length === 1 ? "item" : "items"}`
  if (value && typeof value === "object") {
    const count = Object.keys(value).length
    return `${count} ${count === 1 ? "field" : "fields"}`
  }
  if (value === undefined || value === null || value === "") return "No details"
  return "1 value"
}

function StatusBadge(props: { value: string }) {
  const colors: Record<string, string> = {
    active: "bg-green-500/15 text-text-success",
    paused: "bg-yellow-500/15 text-text-warning",
    completed: "bg-blue-500/15 text-text-info",
  }
  return (
    <span
      class={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${colors[props.value] ?? "bg-background-stronger text-text-dimmed"}`}
    >
      {props.value}
    </span>
  )
}
